import * as XLSXModule from 'xlsx-js-style'
import JSZip from 'jszip'
import { fetchValidacaoSellout } from './service'
import { ValidacaoSelloutParams, ValidacaoSelloutResponse } from './types'
import { buildCorporateSheet } from '../../utils/excelCorporateStyle'

const XLSX = (XLSXModule as any).default || XLSXModule

// =============================================================================
// PALETA DE CORES CORPORATIVA GID
// =============================================================================

const COLORS = {
  headerBg: '0F172A',      // Slate 900 (Dark Slate GID)
  headerSubBg: '1E293B',   // Slate 800 (Navy Slate)
  metaBg: '334155',        // Slate 700
  limeGid: 'A6C10B',       // Verde Neon Corporativo GID
  textDark: '0F172A',      // Texto escuro
  textMuted: '64748B',     // Texto secundário
  textWhite: 'FFFFFF',     // Branco
  borderSubtle: 'E2E8F0',  // Borda suave de células
  borderHeader: '475569',  // Borda entre colunas
  zebraBg: 'F8FAFC',       // Fundo alternado linhas pares
  totalBg: 'E2E8F0',       // Fundo linha de total
  totalBorder: '0F172A',   // Borda dupla contábil

  // Variações
  varPositivaBg: 'DCFCE7',
  varPositivaText: '166534',
  varNegativaBg: 'FEE2E2',
  varNegativaText: '991B1B',
  varNeutraBg: 'F1F5F9',
  varNeutraText: '475569',
}

export const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril',
  'Maio', 'Junho', 'Julho', 'Agosto',
  'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const getRowBorder = () => ({
  top: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  bottom: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  left: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  right: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
})

const getTotalBorder = () => ({
  top: { style: 'thin', color: { rgb: '64748B' } },
  bottom: { style: 'double', color: { rgb: COLORS.totalBorder } },
  left: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  right: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
})

const cellBannerTitle = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: COLORS.headerBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
})

const cellBannerSubtitle = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: COLORS.limeGid } },
    fill: { fgColor: { rgb: COLORS.headerSubBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
})

const cellBannerMeta = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 9.5, color: { rgb: 'F1F5F9' } },
    fill: { fgColor: { rgb: COLORS.metaBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
})

const cellYellowGuideline = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: '713F12' } },
    fill: { fgColor: { rgb: 'FEF08A' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: 'CA8A04' } },
      bottom: { style: 'thin', color: { rgb: 'CA8A04' } },
      left: { style: 'thin', color: { rgb: 'CA8A04' } },
      right: { style: 'thin', color: { rgb: 'CA8A04' } },
    },
  },
})

const cellColHeader = (val: string, align: 'left' | 'center' | 'right' = 'center') => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: COLORS.headerSubBg } },
    alignment: { horizontal: align, vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: '64748B' } },
      bottom: { style: 'medium', color: { rgb: COLORS.limeGid } },
      left: { style: 'thin', color: { rgb: COLORS.borderHeader } },
      right: { style: 'thin', color: { rgb: COLORS.borderHeader } },
    },
  },
})

const cellText = (
  val: any,
  isEven = false,
  align: 'left' | 'center' | 'right' = 'left',
  bold = false
) => ({
  v: val !== null && val !== undefined ? String(val) : '',
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: align, vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellMoney = (val: number, isEven = false, bold = false) => ({
  v: Number((val || 0).toFixed(2)),
  t: 'n' as const,
  z: '"R$" #,##0.00',
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellNumber = (val: number, isEven = false, bold = false) => ({
  v: Math.round(val || 0),
  t: 'n' as const,
  z: '#,##0',
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellPercentWithAlert = (val: number | undefined, isEven = false) => {
  if (val === undefined || val === null || isNaN(val)) {
    return {
      v: '—',
      t: 's' as const,
      s: {
        font: { name: 'Calibri', sz: 9.5, color: { rgb: COLORS.textMuted } },
        fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
        alignment: { horizontal: 'right', vertical: 'center' },
        border: getRowBorder(),
      },
    }
  }

  const isPos = val > 0
  const isNeg = val < 0
  const bg = isPos ? COLORS.varPositivaBg : isNeg ? COLORS.varNegativaBg : (isEven ? COLORS.zebraBg : 'FFFFFF')
  const fg = isPos ? COLORS.varPositivaText : isNeg ? COLORS.varNegativaText : COLORS.textDark

  return {
    v: Number((val / 100).toFixed(4)),
    t: 'n' as const,
    z: '+0.0%;-0.0%;0.0%',
    s: {
      font: { name: 'Calibri', sz: 9.5, bold: Math.abs(val) >= 5, color: { rgb: fg } },
      fill: { fgColor: { rgb: bg } },
      alignment: { horizontal: 'right', vertical: 'center' },
      border: getRowBorder(),
    },
  }
}

const cellTotalLabel = (val: string, align: 'left' | 'center' | 'right' = 'left') => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: align, vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalMoney = (val: number) => ({
  v: Number((val || 0).toFixed(2)),
  t: 'n' as const,
  z: '"R$" #,##0.00',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalNumber = (val: number) => ({
  v: Math.round(val || 0),
  t: 'n' as const,
  z: '#,##0',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalPercent = (val: number | undefined) => {
  if (val === undefined || val === null || isNaN(val)) {
    return {
      v: '—',
      t: 's' as const,
      s: {
        font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textMuted } },
        fill: { fgColor: { rgb: COLORS.totalBg } },
        alignment: { horizontal: 'right', vertical: 'center' },
        border: getTotalBorder(),
      },
    }
  }

  const isPos = val > 0
  const isNeg = val < 0
  const fg = isPos ? COLORS.varPositivaText : isNeg ? COLORS.varNegativaText : COLORS.textDark

  return {
    v: Number((val / 100).toFixed(4)),
    t: 'n' as const,
    z: '+0.0%;-0.0%;0.0%',
    s: {
      font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: fg } },
      fill: { fgColor: { rgb: COLORS.totalBg } },
      alignment: { horizontal: 'right', vertical: 'center' },
      border: getTotalBorder(),
    },
  }
}

// =============================================================================
// CATEGORIZAÇÃO CORPORATIVA CANÔNICA & CÉLULAS DE TOTALIZADORES DE CATEGORIA
// =============================================================================

export const CATEGORIAS_ORDEM = [
  'Macarrão',
  'Refresco',
  'Tempero',
  'Salgadinho',
  'Batata Palha',
  'Snacks',
  'Dona Raiz',
  'Outros',
] as const

export type CategoriaPadrao = typeof CATEGORIAS_ORDEM[number]

/**
 * Classifica qualquer Família ou SKU na categoria oficial canônica
 * seguindo as regras oficiais das abas 'Dados para Plano Venda Direta' e 'Cobertura Ano Categorias'.
 */
export const determinarCategoria = (
  familia: string = '',
  descricao: string = '',
  marca: string = ''
): CategoriaPadrao => {
  const f = (familia || '').trim().toUpperCase()
  const d = (descricao || '').trim().toUpperCase()
  const m = (marca || '').trim().toUpperCase()

  // 1. Dona Raiz (sempre tem precedência absoluta)
  if (m === 'DONA RAIZ' || f.includes('DONA RAIZ') || d.includes('DONA RAIZ')) {
    return 'Dona Raiz'
  }

  // 2. Refresco (Refreskant, Velly sucos/refrescos, Icebel)
  if (
    m === 'REFRESKANT' ||
    f.includes('REFRESKANT') ||
    d.includes('REFRESKANT') ||
    f.includes('ICEBEL') ||
    d.includes('ICEBEL') ||
    (m === 'VELLY' && (d.includes('REF ') || d.includes('REF.') || d.includes('REFRESCO') || d.includes('SUCO') || f.includes('REFRESCO') || f.includes('SUCO'))) ||
    f.includes('REFRESCO') ||
    d.includes('REFRESCO') ||
    f === 'MIX VELLY'
  ) {
    return 'Refresco'
  }

  // 3. Salgadinho (Sanditos sem Palha)
  if (
    ((m === 'SANDITOS' || f.includes('SANDITOS') || d.includes('SANDITOS')) && !f.includes('PALHA') && !d.includes('PALHA')) ||
    (f.includes('SNACK DE TRIGO') && !f.includes('PALHA'))
  ) {
    return 'Salgadinho'
  }

  // 4. Snacks (SanChips sem Palha)
  if (
    ((m === 'SANCHIPS' || m === 'SAN CHIPS' || f.includes('SAN CHIPS') || f.includes('SANCHIPS') || d.includes('SAN CHIPS') || d.includes('SANCHIPS')) && !f.includes('PALHA') && !d.includes('PALHA'))
  ) {
    return 'Snacks'
  }

  // 5. Batata Palha (qualquer marca que seja Batata Palha)
  if (f.includes('PALHA') || d.includes('PALHA')) {
    return 'Batata Palha'
  }

  // 6. Macarrão (Sandella, Yolle, exceto Dona Raiz já filtrada acima)
  if (f.includes('MACARR') || d.includes('MACARR')) {
    return 'Macarrão'
  }

  // 7. Tempero (Sandella temperos e condimentos, Caldo, Yolle)
  if (
    f.includes('TEMPERO') ||
    d.includes('TEMPERO') ||
    f.includes('CONDIMENTO') ||
    d.includes('CONDIMENTO') ||
    f.includes('CALDO') ||
    d.includes('CALDO')
  ) {
    return 'Tempero'
  }

  // 8. Outros (farinhas, sopão, mistura de bolo, azeites, pipocas, amendoim, etc.)
  return 'Outros'
}

const cellCategoryHeader = (val: string, align: 'left' | 'center' | 'right' = 'left') => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: '334155' } }, // Slate 700 - Subtotal corporativo
    alignment: { horizontal: align, vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '475569' } },
      bottom: { style: 'thin', color: { rgb: '475569' } },
      left: { style: 'thin', color: { rgb: '475569' } },
      right: { style: 'thin', color: { rgb: '475569' } },
    },
  },
})

const cellCategoryMoney = (val: number) => ({
  v: Number((val || 0).toFixed(2)),
  t: 'n' as const,
  z: '"R$" #,##0.00',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: '334155' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '475569' } },
      bottom: { style: 'thin', color: { rgb: '475569' } },
      left: { style: 'thin', color: { rgb: '475569' } },
      right: { style: 'thin', color: { rgb: '475569' } },
    },
  },
})

const cellCategoryNumber = (val: number) => ({
  v: Math.round(val || 0),
  t: 'n' as const,
  z: '#,##0',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: '334155' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '475569' } },
      bottom: { style: 'thin', color: { rgb: '475569' } },
      left: { style: 'thin', color: { rgb: '475569' } },
      right: { style: 'thin', color: { rgb: '475569' } },
    },
  },
})

const cellCategoryPercent = (val: number | undefined) => {
  if (val === undefined || val === null || isNaN(val)) {
    return {
      v: '—',
      t: 's' as const,
      s: {
        font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: '94A3B8' } },
        fill: { fgColor: { rgb: '334155' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '475569' } },
          bottom: { style: 'thin', color: { rgb: '475569' } },
          left: { style: 'thin', color: { rgb: '475569' } },
          right: { style: 'thin', color: { rgb: '475569' } },
        },
      },
    }
  }
  const isPos = val > 0
  const isNeg = val < 0
  const textColor = isPos ? '86EFAC' : isNeg ? 'FCA5A5' : 'FFFFFF'
  return {
    v: Number((val / 100).toFixed(4)),
    t: 'n' as const,
    z: '+0.0%;-0.0%;0.0%',
    s: {
      font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: textColor } },
      fill: { fgColor: { rgb: '334155' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '475569' } },
        bottom: { style: 'thin', color: { rgb: '475569' } },
        left: { style: 'thin', color: { rgb: '475569' } },
        right: { style: 'thin', color: { rgb: '475569' } },
      },
    },
  }
}

const cellCategoryShare = (val: number) => ({
  v: Number(((val || 0) / 100).toFixed(4)),
  t: 'n' as const,
  z: '0.0%',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.limeGid } },
    fill: { fgColor: { rgb: '334155' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '475569' } },
      bottom: { style: 'thin', color: { rgb: '475569' } },
      left: { style: 'thin', color: { rgb: '475569' } },
      right: { style: 'thin', color: { rgb: '475569' } },
    },
  },
})

// =============================================================================
// CONSTRUÇÃO DO WORKBOOK EXCEL INDIVIDUAL POR DISTRIBUIDOR
// =============================================================================

export const buildDistribuidorExcelWorkbook = (
  distName: string,
  distCode: string | number | undefined,
  periodLabel: string,
  semanasLabel: string,
  data: ValidacaoSelloutResponse
): Uint8Array => {
  const wb = XLSX.utils.book_new()
  const sheetData: any[][] = []

  // 1. Banner Superior
  sheetData.push([cellBannerTitle('GID TRADE MARKETING — REVISÃO DE SELLOUT (CATEGORIAS)')])
  sheetData.push([
    cellBannerSubtitle(
      `DISTRIBUIDOR: ${distName.toUpperCase()} ${distCode ? `(#${distCode})` : ''} — ${periodLabel.toUpperCase()} (${semanasLabel})`
    ),
  ])
  sheetData.push([
    cellBannerMeta(
      `Extração: ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} | Comparativo: Sellout Atual vs Média dos Últimos 3 Meses Fechados`
    ),
  ])

  // 2. Cabeçalho das Colunas
  sheetData.push([
    cellColHeader('Família / Categoria', 'left'),
    cellColHeader('Sellout Atual (R$)', 'right'),
    cellColHeader('Média 3M (R$)', 'right'),
    cellColHeader('Var. R$ (%)', 'right'),
    cellColHeader('Sellout Atual (CX)', 'right'),
    cellColHeader('Média 3M (CX)', 'right'),
    cellColHeader('Var. CX (%)', 'right'),
  ])

  // 3. Linhas de Dados (agrupadas por Categoria com quebra por Família e SKU)
  const itens = data.selloutPorFamilia || []
  const catGroupsDist: Record<string, typeof itens> = {}
  CATEGORIAS_ORDEM.forEach((cat) => { catGroupsDist[cat] = [] })

  itens.forEach((item) => {
    const firstSkuDesc = item.skus?.[0]?.descricao || ''
    const cat = determinarCategoria(item.familia, firstSkuDesc)
    if (!catGroupsDist[cat]) catGroupsDist[cat] = []
    catGroupsDist[cat].push(item)
  })

  let distRowIdx = 0
  CATEGORIAS_ORDEM.forEach((catNome) => {
    const fams = catGroupsDist[catNome] || []
    if (fams.length === 0) return

    let catRS = 0
    let catMediaRS = 0
    let catCX = 0
    let catMediaCX = 0

    fams.forEach((f) => {
      catRS += f.valorRS || 0
      catMediaRS += f.media_3m_valor || 0
      catCX += f.caixas || 0
      catMediaCX += f.media_3m_caixas || 0
    })

    const varCatRS = catMediaRS > 0 ? ((catRS - catMediaRS) / catMediaRS) * 100 : undefined
    const varCatCX = catMediaCX > 0 ? ((catCX - catMediaCX) / catMediaCX) * 100 : undefined

    // Linha de Total da Categoria
    sheetData.push([
      cellCategoryHeader(`CATEGORIA: ${catNome.toUpperCase()}`),
      cellCategoryMoney(catRS),
      cellCategoryMoney(catMediaRS),
      cellCategoryPercent(varCatRS),
      cellCategoryNumber(catCX),
      cellCategoryNumber(catMediaCX),
      cellCategoryPercent(varCatCX),
    ])

    fams.forEach((item) => {
      const isEven = distRowIdx % 2 === 1
      distRowIdx++
      sheetData.push([
        cellText(`  ▸ ${item.familia}`, isEven, 'left', true),
        cellMoney(item.valorRS, isEven),
        cellMoney(item.media_3m_valor || 0, isEven),
        cellPercentWithAlert(item.variacao_valor_percentual, isEven),
        cellNumber(item.caixas, isEven),
        cellNumber(item.media_3m_caixas || 0, isEven),
        cellPercentWithAlert(item.variacao_percentual, isEven),
      ])
      if (item.skus && item.skus.length > 0) {
        item.skus.forEach((s) => {
          sheetData.push([
            cellText(`      ↳ [${s.sku}] ${s.descricao}`, isEven, 'left', false),
            cellMoney(s.valorRS, isEven),
            cellMoney(s.media_3m_valor || 0, isEven),
            cellPercentWithAlert(s.variacao_valor_percentual, isEven),
            cellNumber(s.caixas, isEven),
            cellNumber(s.media_3m_caixas || 0, isEven),
            cellPercentWithAlert(s.variacao_percentual, isEven),
          ])
        })
      }
    })
  })

  // 4. Linha de Total Geral
  const totalRS = data.totalSelloutRS || 0
  const totalMediaRS = data.resumo?.sellout_media_3m_valor || 0
  const varTotalRS = data.resumo?.sellout_variacao_valor_percentual !== undefined
    ? data.resumo.sellout_variacao_valor_percentual
    : totalMediaRS > 0 ? ((totalRS - totalMediaRS) / totalMediaRS) * 100 : undefined

  const totalCX = data.totalSelloutCX || 0
  const totalMediaCX = data.resumo?.sellout_media_3m_caixas || 0
  const varTotalCX = data.resumo?.sellout_variacao_percentual !== undefined
    ? data.resumo.sellout_variacao_percentual
    : totalMediaCX > 0 ? ((totalCX - totalMediaCX) / totalMediaCX) * 100 : undefined

  sheetData.push([
    cellTotalLabel('TOTAL GERAL', 'left'),
    cellTotalMoney(totalRS),
    cellTotalMoney(totalMediaRS),
    cellTotalPercent(varTotalRS),
    cellTotalNumber(totalCX),
    cellTotalNumber(totalMediaCX),
    cellTotalPercent(varTotalCX),
  ])

  const ws = XLSX.utils.aoa_to_sheet(sheetData)

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } },
  ]

  ws['!cols'] = [
    { wch: 36 }, // Família / SKU
    { wch: 22 }, // Sellout Atual R$
    { wch: 20 }, // Média 3M R$
    { wch: 16 }, // Var. R$ %
    { wch: 18 }, // Sellout Atual CX
    { wch: 18 }, // Média 3M CX
    { wch: 16 }, // Var. CX %
  ]

  const totalDataRows = sheetData.length - 5 // Descontando 3 banners, 1 header, 1 total
  ws['!rows'] = [
    { hpt: 28 }, // Banner 1
    { hpt: 20 }, // Banner 2
    { hpt: 18 }, // Banner 3
    { hpt: 24 }, // Header
    ...Array(Math.max(0, totalDataRows)).fill({ hpt: 20 }),
    { hpt: 22 }, // Total
  ]

  XLSX.utils.book_append_sheet(wb, ws, 'Sellout vs Média 3M')

  const arrayBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return new Uint8Array(arrayBuffer)
}

/**
 * Constrói a planilha corporativa no exato formato da tela de validação individual (4 abas: Resumo, Categorias, Estoque e Cobertura com Potencial/Gap)
 */
export const buildResumoCorporativoWorkbook = (
  distLabel: string,
  periodLabel: string,
  data: ValidacaoSelloutResponse,
): Uint8Array => {
  const wb = XLSX.utils.book_new()

  // 1. Resumo
  const rowsResumo = [
    ['Total Sellout Faturado (R$)', data.totalSelloutRS],
    ['Total Sellout Comercializado (CX)', data.totalSelloutCX],
    ['Meta de Sellout Estabelecida (R$)', data.resumo?.sellout_meta_valor ?? 0],
    ['Meta de Sellout em Volume (CX)', data.resumo?.sellout_meta_caixas ?? 0],
    ['Clientes Positivados Realizados', data.resumo?.cobertura_total_clientes ?? 0],
    ['Potencial de Clientes (Cidades Atuação)', data.resumo?.cobertura_potencial_clientes ?? 0],
    ['Gap de Cobertura de Clientes', data.resumo?.cobertura_gap_clientes ?? 0],
  ]
  const wsResumo = buildCorporateSheet({
    reportTitle: 'Validação de Sellout — Resumo Consolidado',
    periodLabel,
    distributorLabel: distLabel,
    headers: [
      { label: 'Indicador de Sellout', align: 'left' },
      { label: 'Valor Consolidado', align: 'right' },
    ],
    data: rowsResumo,
  })
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo')

  // 2. Categorias / Famílias (com quebra por SKU e Totalizador por Categoria)
  const rowsSell: (string | number | any)[][] = []
  const catGroupsSell: Record<string, typeof data.selloutPorFamilia> = {}
  CATEGORIAS_ORDEM.forEach((cat) => { catGroupsSell[cat] = [] })

  data.selloutPorFamilia.forEach((i) => {
    const firstSkuDesc = i.skus?.[0]?.descricao || ''
    const cat = determinarCategoria(i.familia, firstSkuDesc)
    if (!catGroupsSell[cat]) catGroupsSell[cat] = []
    catGroupsSell[cat].push(i)
  })

  let sellRowIdx = 0
  CATEGORIAS_ORDEM.forEach((catNome) => {
    const fams = catGroupsSell[catNome] || []
    if (fams.length === 0) return

    let catValorRS = 0
    let catCaixas = 0
    let catMetaValor = 0
    let catMetaCaixas = 0
    let catMedia3MValor = 0
    let catMedia3MCaixas = 0

    fams.forEach((f) => {
      catValorRS += f.valorRS || 0
      catCaixas += f.caixas || 0
      catMetaValor += f.meta_valor ?? 0
      catMetaCaixas += f.meta_caixas ?? 0
      catMedia3MValor += f.media_3m_valor ?? 0
      catMedia3MCaixas += f.media_3m_caixas ?? 0
    })

    const varMetaValorPct = catMetaValor > 0 ? ((catValorRS - catMetaValor) / catMetaValor) * 100 : undefined
    const varMetaCaixasPct = catMetaCaixas > 0 ? ((catCaixas - catMetaCaixas) / catMetaCaixas) * 100 : undefined
    const varMedia3MValorPct = catMedia3MValor > 0 ? ((catValorRS - catMedia3MValor) / catMedia3MValor) * 100 : undefined
    const varMedia3MCaixasPct = catMedia3MCaixas > 0 ? ((catCaixas - catMedia3MCaixas) / catMedia3MCaixas) * 100 : undefined
    const catShare = data.totalSelloutRS > 0 ? (catValorRS / data.totalSelloutRS) * 100 : 0

    // Linha de Totalizador da Categoria
    rowsSell.push([
      cellCategoryHeader(`CATEGORIA: ${catNome.toUpperCase()}`),
      cellCategoryMoney(catValorRS),
      cellCategoryNumber(catCaixas),
      cellCategoryMoney(catMetaValor),
      cellCategoryNumber(catMetaCaixas),
      cellCategoryPercent(varMetaValorPct),
      cellCategoryPercent(varMetaCaixasPct),
      cellCategoryMoney(catMedia3MValor),
      cellCategoryPercent(varMedia3MValorPct),
      cellCategoryNumber(catMedia3MCaixas),
      cellCategoryPercent(varMedia3MCaixasPct),
      cellCategoryShare(catShare),
    ])

    fams.forEach((i) => {
      const isEven = sellRowIdx % 2 === 1
      sellRowIdx++
      rowsSell.push([
        cellText(`  ▸ ${i.familia}`, isEven, 'left', true),
        cellMoney(i.valorRS, isEven),
        cellNumber(i.caixas, isEven),
        cellMoney(i.meta_valor ?? 0, isEven),
        cellNumber(i.meta_caixas ?? 0, isEven),
        cellPercentWithAlert(i.variacao_meta_valor_percentual, isEven),
        cellPercentWithAlert(i.variacao_meta_percentual, isEven),
        cellMoney(i.media_3m_valor ?? 0, isEven),
        cellPercentWithAlert(i.variacao_valor_percentual, isEven),
        cellNumber(i.media_3m_caixas ?? 0, isEven),
        cellPercentWithAlert(i.variacao_percentual, isEven),
        cellPercentWithAlert(i.sharePercent, isEven),
      ])
      if (i.skus && i.skus.length > 0) {
        i.skus.forEach((s) => {
          rowsSell.push([
            cellText(`      ↳ [${s.sku}] ${s.descricao}`, isEven, 'left', false),
            cellMoney(s.valorRS, isEven),
            cellNumber(s.caixas, isEven),
            cellText('—', isEven, 'center'),
            cellText('—', isEven, 'center'),
            cellText('—', isEven, 'center'),
            cellText('—', isEven, 'center'),
            cellMoney(s.media_3m_valor ?? 0, isEven),
            cellPercentWithAlert(s.variacao_valor_percentual, isEven),
            cellNumber(s.media_3m_caixas ?? 0, isEven),
            cellPercentWithAlert(s.variacao_percentual, isEven),
            cellPercentWithAlert(s.sharePercent, isEven),
          ])
        })
      }
    })
  })
  const totalSell = [
    'TOTAL GERAL',
    data.totalSelloutRS,
    data.totalSelloutCX,
    data.resumo?.sellout_meta_valor ?? 0,
    data.resumo?.sellout_meta_caixas ?? 0,
    data.resumo?.sellout_variacao_meta_valor_percentual !== undefined ? `${data.resumo.sellout_variacao_meta_valor_percentual}%` : '—',
    data.resumo?.sellout_variacao_meta_percentual !== undefined ? `${data.resumo.sellout_variacao_meta_percentual}%` : '—',
    data.resumo?.sellout_media_3m_valor ?? 0,
    data.resumo?.sellout_variacao_valor_percentual !== undefined ? `${data.resumo.sellout_variacao_valor_percentual}%` : '—',
    data.resumo?.sellout_media_3m_caixas ?? 0,
    data.resumo?.sellout_variacao_percentual !== undefined ? `${data.resumo.sellout_variacao_percentual}%` : '—',
    100,
  ]
  const headersSell = [
    { label: 'Família / Categoria', align: 'left' as const },
    { label: 'Sellout (R$)', align: 'right' as const },
    { label: 'Sellout (CX)', align: 'right' as const },
    { label: 'Meta (R$)', align: 'right' as const },
    { label: 'Meta (CX)', align: 'right' as const },
    { label: 'Var. Meta % (R$)', align: 'center' as const },
    { label: 'Var. Meta % (CX)', align: 'center' as const },
    { label: 'Média 3M (R$)', align: 'right' as const },
    { label: 'Var. 3M % (R$)', align: 'center' as const },
    { label: 'Média 3M (CX)', align: 'right' as const },
    { label: 'Var. 3M % (CX)', align: 'center' as const },
    { label: 'Share (%)', align: 'right' as const },
  ]
  const wsSell = buildCorporateSheet({
    reportTitle: 'Validação de Sellout — Desempenho por Categoria e SKU',
    periodLabel,
    distributorLabel: distLabel,
    headers: headersSell,
    data: rowsSell,
    totalsRow: totalSell,
  })
  XLSX.utils.book_append_sheet(wb, wsSell, 'Categorias')

  // 3. Estoque (com quebra por SKU e Totalizador por Categoria)
  const totalEstCX = Number(data.resumo?.estoque_total_caixas ?? data.totalEstoqueCX ?? 0)
  const totalEstRS = Number(data.resumo?.estoque_total_valor ?? data.totalEstoqueRS ?? 0)
  const rowsEst: (string | number | any)[][] = []

  const catGroupsEst: Record<string, typeof data.estoquePorFamilia> = {}
  CATEGORIAS_ORDEM.forEach((cat) => { catGroupsEst[cat] = [] })

  data.estoquePorFamilia.forEach((i) => {
    const firstSkuDesc = i.skus?.[0]?.descricao || ''
    const cat = determinarCategoria(i.familia, firstSkuDesc)
    if (!catGroupsEst[cat]) catGroupsEst[cat] = []
    catGroupsEst[cat].push(i)
  })

  let estRowIdx = 0
  CATEGORIAS_ORDEM.forEach((catNome) => {
    const fams = catGroupsEst[catNome] || []
    if (fams.length === 0) return

    let catCaixas = 0
    let catValorRS = 0
    let catMedia3MCX = 0

    fams.forEach((f) => {
      catCaixas += f.caixas || 0
      catValorRS += f.valorRS || 0
      catMedia3MCX += f.media_3m_caixas ?? 0
    })

    const varCatPct = catMedia3MCX > 0 ? ((catCaixas - catMedia3MCX) / catMedia3MCX) * 100 : undefined

    // Linha de Totalizador da Categoria
    rowsEst.push([
      cellCategoryHeader(`CATEGORIA: ${catNome.toUpperCase()}`),
      cellCategoryNumber(catCaixas),
      cellCategoryMoney(catValorRS),
      cellCategoryNumber(catMedia3MCX),
      cellCategoryPercent(varCatPct),
    ])

    fams.forEach((i) => {
      const isEven = estRowIdx % 2 === 1
      estRowIdx++
      rowsEst.push([
        cellText(`  ▸ ${i.familia}`, isEven, 'left', true),
        cellNumber(i.caixas, isEven),
        cellMoney(i.valorRS, isEven),
        cellNumber(i.media_3m_caixas ?? 0, isEven),
        cellPercentWithAlert(i.variacao_percentual, isEven),
      ])
      if (i.skus && i.skus.length > 0) {
        i.skus.forEach((s) => {
          rowsEst.push([
            cellText(`      ↳ [${s.sku}] ${s.descricao}`, isEven, 'left', false),
            cellNumber(s.caixas, isEven),
            cellMoney(s.valorRS, isEven),
            cellNumber(s.media_3m_caixas ?? 0, isEven),
            cellPercentWithAlert(s.variacao_percentual, isEven),
          ])
        })
      }
    })
  })
  const totalEst = [
    'TOTAL GERAL',
    totalEstCX,
    totalEstRS,
    data.resumo?.estoque_media_3m_caixas ?? 0,
    data.resumo?.estoque_variacao_percentual !== undefined ? `${data.resumo.estoque_variacao_percentual}%` : '—',
  ]
  const headersEst = [
    { label: 'Família / Categoria', align: 'left' as const },
    { label: 'Estoque (CX)', align: 'right' as const },
    { label: 'Estoque (R$)', align: 'right' as const },
    { label: 'Média 3M (CX)', align: 'right' as const },
    { label: 'Var. % CX', align: 'center' as const },
  ]
  const wsEst = buildCorporateSheet({
    reportTitle: 'Validação de Estoque — Posição por Categoria e SKU',
    periodLabel,
    distributorLabel: distLabel,
    headers: headersEst,
    data: rowsEst,
    totalsRow: totalEst,
  })
  XLSX.utils.book_append_sheet(wb, wsEst, 'Estoque')

  // 4. Cobertura (com quebra por SKU e Totalizador por Categoria)
  const totalCob = Number(
    data.resumo?.cobertura_total_clientes ??
      data.coberturaPorFamilia.reduce((a, b) => a + b.clientesPositivados, 0),
  )
  const rowsCob: (string | number | any)[][] = []

  const catGroupsCob: Record<string, typeof data.coberturaPorFamilia> = {}
  CATEGORIAS_ORDEM.forEach((cat) => { catGroupsCob[cat] = [] })

  data.coberturaPorFamilia.forEach((i) => {
    const firstSkuDesc = i.skus?.[0]?.descricao || ''
    const cat = determinarCategoria(i.familia, firstSkuDesc)
    if (!catGroupsCob[cat]) catGroupsCob[cat] = []
    catGroupsCob[cat].push(i)
  })

  let cobRowIdx = 0
  CATEGORIAS_ORDEM.forEach((catNome) => {
    const fams = catGroupsCob[catNome] || []
    if (fams.length === 0) return

    let catCob = 0
    let catMedia3M = 0

    fams.forEach((f) => {
      catCob += f.clientesPositivados || 0
      catMedia3M += f.media_3m_clientes ?? 0
    })

    const varCatPct = catMedia3M > 0 ? ((catCob - catMedia3M) / catMedia3M) * 100 : undefined

    // Linha de Totalizador da Categoria
    rowsCob.push([
      cellCategoryHeader(`CATEGORIA: ${catNome.toUpperCase()}`),
      cellCategoryNumber(catCob),
      cellCategoryHeader('—', 'center'),
      cellCategoryHeader('—', 'center'),
      cellCategoryNumber(catMedia3M),
      cellCategoryPercent(varCatPct),
    ])

    fams.forEach((i) => {
      const isEven = cobRowIdx % 2 === 1
      cobRowIdx++
      rowsCob.push([
        cellText(`  ▸ ${i.familia}`, isEven, 'left', true),
        cellNumber(i.clientesPositivados, isEven),
        cellText(i.potencial_clientes ?? '—', isEven, 'right'),
        cellText(i.gap_clientes ?? '—', isEven, 'right'),
        cellNumber(i.media_3m_clientes ?? 0, isEven),
        cellPercentWithAlert(i.variacao_percentual, isEven),
      ])
      if (i.skus && i.skus.length > 0) {
        i.skus.forEach((s) => {
          rowsCob.push([
            cellText(`      ↳ [${s.sku}] ${s.descricao}`, isEven, 'left', false),
            cellNumber(s.clientesPositivados, isEven),
            cellText('—', isEven, 'center'),
            cellText('—', isEven, 'center'),
            cellNumber(s.media_3m_clientes ?? 0, isEven),
            cellPercentWithAlert(s.variacao_percentual, isEven),
          ])
        })
      }
    })
  })
  const totalCobRow = [
    'TOTAL GERAL',
    totalCob,
    data.resumo?.cobertura_potencial_clientes ?? '—',
    data.resumo?.cobertura_gap_clientes ?? '—',
    data.resumo?.cobertura_media_3m_clientes ?? 0,
    data.resumo?.cobertura_variacao_percentual !== undefined ? `${data.resumo.cobertura_variacao_percentual}%` : '—',
  ]
  const headersCob = [
    { label: 'Família / Categoria', align: 'left' as const },
    { label: 'Clientes Positivados', align: 'right' as const },
    { label: 'Potencial (Cidades Atuação)', align: 'right' as const },
    { label: 'Gap de Cobertura', align: 'right' as const },
    { label: 'Média 3M Clientes', align: 'right' as const },
    { label: 'Var. %', align: 'center' as const },
  ]
  const wsCob = buildCorporateSheet({
    reportTitle: 'Validação de Cobertura — Positivação de Clientes por Categoria e SKU',
    periodLabel,
    distributorLabel: distLabel,
    headers: headersCob,
    data: rowsCob,
    totalsRow: totalCobRow,
  })
  XLSX.utils.book_append_sheet(wb, wsCob, 'Resumo Cobertura')

  // 5. Visão Consolidada de Todos os SKUs
  const skuMap: Record<
    string,
    {
      familia: string
      sku: string
      descricao: string
      selloutRS: number
      selloutCX: number
      shareCat: number
      media3mSelloutRS: number
      var3mSelloutRS: string | number
      media3mSelloutCX: number
      var3mSelloutCX: string | number
      estoqueCX: number
      estoqueRS: number
      media3mEstoqueCX: number
      var3mEstoqueCX: string | number
      coberturaClientes: number
      media3mCobClientes: number
      var3mCobClientes: string | number
    }
  > = {}

  data.selloutPorFamilia.forEach((f) => {
    ;(f.skus || []).forEach((s) => {
      skuMap[s.sku] = {
        familia: f.familia,
        sku: s.sku,
        descricao: s.descricao,
        selloutRS: s.valorRS,
        selloutCX: s.caixas,
        shareCat: s.sharePercent,
        media3mSelloutRS: s.media_3m_valor ?? 0,
        var3mSelloutRS: s.variacao_valor_percentual !== undefined ? `${s.variacao_valor_percentual}%` : '—',
        media3mSelloutCX: s.media_3m_caixas ?? 0,
        var3mSelloutCX: s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
        estoqueCX: 0,
        estoqueRS: 0,
        media3mEstoqueCX: 0,
        var3mEstoqueCX: '—',
        coberturaClientes: s.clientesPositivados ?? 0,
        media3mCobClientes: 0,
        var3mCobClientes: '—',
      }
    })
  })

  data.estoquePorFamilia.forEach((f) => {
    ;(f.skus || []).forEach((s) => {
      if (!skuMap[s.sku]) {
        skuMap[s.sku] = {
          familia: f.familia,
          sku: s.sku,
          descricao: s.descricao,
          selloutRS: 0,
          selloutCX: 0,
          shareCat: 0,
          media3mSelloutRS: 0,
          var3mSelloutRS: '—',
          media3mSelloutCX: 0,
          var3mSelloutCX: '—',
          estoqueCX: s.caixas,
          estoqueRS: s.valorRS,
          media3mEstoqueCX: s.media_3m_caixas ?? 0,
          var3mEstoqueCX: s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—',
          coberturaClientes: 0,
          media3mCobClientes: 0,
          var3mCobClientes: '—',
        }
      } else {
        skuMap[s.sku].estoqueCX = s.caixas
        skuMap[s.sku].estoqueRS = s.valorRS
        skuMap[s.sku].media3mEstoqueCX = s.media_3m_caixas ?? 0
        skuMap[s.sku].var3mEstoqueCX = s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—'
      }
    })
  })

  data.coberturaPorFamilia.forEach((f) => {
    ;(f.skus || []).forEach((s) => {
      if (skuMap[s.sku]) {
        if (skuMap[s.sku].coberturaClientes === 0) {
          skuMap[s.sku].coberturaClientes = s.clientesPositivados
        }
        skuMap[s.sku].media3mCobClientes = s.media_3m_clientes ?? 0
        skuMap[s.sku].var3mCobClientes = s.variacao_percentual !== undefined ? `${s.variacao_percentual}%` : '—'
      }
    })
  })

  // Agrupamento dos SKUs por Categoria com Totalizador
  const rowsSkus: (string | number | any)[][] = []
  const catGroupsSkus: Record<string, any[]> = {}
  CATEGORIAS_ORDEM.forEach((cat) => { catGroupsSkus[cat] = [] })

  Object.values(skuMap).forEach((s) => {
    const cat = determinarCategoria(s.familia, s.descricao)
    if (!catGroupsSkus[cat]) catGroupsSkus[cat] = []
    catGroupsSkus[cat].push(s)
  })

  let skuRowIdx = 0
  CATEGORIAS_ORDEM.forEach((catNome) => {
    const skus = catGroupsSkus[catNome] || []
    if (skus.length === 0) return

    skus.sort((a, b) => b.selloutRS - a.selloutRS)

    let totalCatRS = 0
    let totalCatCX = 0
    let totalCatMedia3MRS = 0
    let totalCatMedia3MCX = 0
    let totalCatEstCX = 0
    let totalCatEstRS = 0
    let totalCatMedia3MEstCX = 0
    let totalCatCob = 0
    let totalCatMedia3MCob = 0

    skus.forEach((s) => {
      totalCatRS += s.selloutRS || 0
      totalCatCX += s.selloutCX || 0
      totalCatMedia3MRS += s.media3mSelloutRS || 0
      totalCatMedia3MCX += s.media3mSelloutCX || 0
      totalCatEstCX += s.estoqueCX || 0
      totalCatEstRS += s.estoqueRS || 0
      totalCatMedia3MEstCX += s.media3mEstoqueCX || 0
      totalCatCob += s.coberturaClientes || 0
      totalCatMedia3MCob += s.media3mCobClientes || 0
    })

    const varCatRS = totalCatMedia3MRS > 0 ? ((totalCatRS - totalCatMedia3MRS) / totalCatMedia3MRS) * 100 : undefined
    const varCatCX = totalCatMedia3MCX > 0 ? ((totalCatCX - totalCatMedia3MCX) / totalCatMedia3MCX) * 100 : undefined
    const varCatEstCX = totalCatMedia3MEstCX > 0 ? ((totalCatEstCX - totalCatMedia3MEstCX) / totalCatMedia3MEstCX) * 100 : undefined
    const varCatCob = totalCatMedia3MCob > 0 ? ((totalCatCob - totalCatMedia3MCob) / totalCatMedia3MCob) * 100 : undefined
    const catShare = data.totalSelloutRS > 0 ? (totalCatRS / data.totalSelloutRS) * 100 : 0

    // Linha de Totalizador da Categoria no SKU
    rowsSkus.push([
      cellCategoryHeader(`CATEGORIA: ${catNome.toUpperCase()}`),
      cellCategoryHeader('—', 'center'),
      cellCategoryHeader(`${skus.length} SKUs`, 'left'),
      cellCategoryMoney(totalCatRS),
      cellCategoryNumber(totalCatCX),
      cellCategoryShare(catShare),
      cellCategoryMoney(totalCatMedia3MRS),
      cellCategoryPercent(varCatRS),
      cellCategoryNumber(totalCatMedia3MCX),
      cellCategoryPercent(varCatCX),
      cellCategoryNumber(totalCatEstCX),
      cellCategoryMoney(totalCatEstRS),
      cellCategoryNumber(totalCatMedia3MEstCX),
      cellCategoryPercent(varCatEstCX),
      cellCategoryNumber(totalCatCob),
      cellCategoryNumber(totalCatMedia3MCob),
      cellCategoryPercent(varCatCob),
    ])

    skus.forEach((s) => {
      const isEven = skuRowIdx % 2 === 1
      skuRowIdx++
      rowsSkus.push([
        cellText(s.familia, isEven, 'left'),
        cellText(s.sku, isEven, 'center'),
        cellText(s.descricao, isEven, 'left'),
        cellMoney(s.selloutRS, isEven),
        cellNumber(s.selloutCX, isEven),
        cellPercentWithAlert(typeof s.shareCat === 'number' ? s.shareCat : parseFloat(String(s.shareCat)) || undefined, isEven),
        cellMoney(s.media3mSelloutRS, isEven),
        cellPercentWithAlert(typeof s.var3mSelloutRS === 'number' ? s.var3mSelloutRS : parseFloat(String(s.var3mSelloutRS)) || undefined, isEven),
        cellNumber(s.media3mSelloutCX, isEven),
        cellPercentWithAlert(typeof s.var3mSelloutCX === 'number' ? s.var3mSelloutCX : parseFloat(String(s.var3mSelloutCX)) || undefined, isEven),
        cellNumber(s.estoqueCX, isEven),
        cellMoney(s.estoqueRS, isEven),
        cellNumber(s.media3mEstoqueCX, isEven),
        cellPercentWithAlert(typeof s.var3mEstoqueCX === 'number' ? s.var3mEstoqueCX : parseFloat(String(s.var3mEstoqueCX)) || undefined, isEven),
        cellNumber(s.coberturaClientes, isEven),
        cellNumber(s.media3mCobClientes, isEven),
        cellPercentWithAlert(typeof s.var3mCobClientes === 'number' ? s.var3mCobClientes : parseFloat(String(s.var3mCobClientes)) || undefined, isEven),
      ])
    })
  })

  const totalSkusRow = [
    'TOTAL GERAL',
    '—',
    `${rowsSkus.length} SKUs`,
    data.totalSelloutRS,
    data.totalSelloutCX,
    100,
    data.resumo?.sellout_media_3m_valor ?? 0,
    data.resumo?.sellout_variacao_valor_percentual !== undefined ? `${data.resumo.sellout_variacao_valor_percentual}%` : '—',
    data.resumo?.sellout_media_3m_caixas ?? 0,
    data.resumo?.sellout_variacao_percentual !== undefined ? `${data.resumo.sellout_variacao_percentual}%` : '—',
    totalEstCX,
    totalEstRS,
    data.resumo?.estoque_media_3m_caixas ?? 0,
    data.resumo?.estoque_variacao_percentual !== undefined ? `${data.resumo.estoque_variacao_percentual}%` : '—',
    totalCob,
    data.resumo?.cobertura_media_3m_clientes ?? 0,
    data.resumo?.cobertura_variacao_percentual !== undefined ? `${data.resumo.cobertura_variacao_percentual}%` : '—',
  ]

  const headersSkus = [
    { label: 'Família / Categoria', align: 'left' as const },
    { label: 'Código SKU', align: 'center' as const },
    { label: 'Descrição do Produto', align: 'left' as const },
    { label: 'Sellout (R$)', align: 'right' as const },
    { label: 'Sellout (CX)', align: 'right' as const },
    { label: 'Share Categoria (%)', align: 'right' as const },
    { label: 'Média 3M (R$)', align: 'right' as const },
    { label: 'Var. 3M % (R$)', align: 'center' as const },
    { label: 'Média 3M (CX)', align: 'right' as const },
    { label: 'Var. 3M % (CX)', align: 'center' as const },
    { label: 'Estoque (CX)', align: 'right' as const },
    { label: 'Estoque (R$)', align: 'right' as const },
    { label: 'Média 3M Estoque (CX)', align: 'right' as const },
    { label: 'Var. % Estoque', align: 'center' as const },
    { label: 'Clientes Positivados', align: 'right' as const },
    { label: 'Média 3M Clientes', align: 'right' as const },
    { label: 'Var. % Clientes', align: 'center' as const },
  ]

  const wsSkus = buildCorporateSheet({
    reportTitle: 'Validação de Sellout — Posição Detalhada por SKU',
    periodLabel,
    distributorLabel: distLabel,
    headers: headersSkus,
    data: rowsSkus,
    totalsRow: totalSkusRow,
  })
  XLSX.utils.book_append_sheet(wb, wsSkus, 'SKUs')

  // 6. Abas Mensais por Distribuidor e Família ('Sellout CX', 'Sellout R$' e 'Cobertura')
  appendCorporateMensalSheets(wb, data, periodLabel, distLabel)

  // 7. Aba 'Dados para Plano Venda Direta' (estrutura conforme modelo de Positivação 2027)
  if (data.planoVendaDireta && data.planoVendaDireta.length > 0) {
    appendCorporatePlanoVendaDiretaSheet(wb, data, periodLabel, distLabel)
  }

  // 8. Aba 'Cobertura Ano Categorias' (positivação única por categoria acumulada no ano + resumo financeiro)
  if (data.coberturaAnoCategorias) {
    appendCorporateCoberturaAnoCategoriasSheet(wb, data, periodLabel, distLabel)
  }

  const arrayBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return new Uint8Array(arrayBuffer)
}

/**
 * Constrói e anexa as 3 abas solicitadas:
 * 1. 'Sellout CX'
 * 2. 'Sellout R$'
 * 3. 'Cobertura'
 * Cada uma com as colunas: Distribuidor Cod, Distribuidor Nome, Família Cod, Família Nome, [Meses Selecionados...], Total
 */
export const appendCorporateMensalSheets = (
  wb: any,
  data: ValidacaoSelloutResponse,
  periodLabel: string,
  distLabel: string = 'Distribuidores Selecionados'
) => {
  const itens = data.mensalDistribuidoresFamilias || []
  const isConsolidado = distLabel.toLowerCase().includes('consolidado')

  // Se não houver itens e não for arquivo consolidado, não adiciona abas vazias
  if (itens.length === 0 && !isConsolidado) {
    return
  }

  // Lista dos meses do período selecionado
  const activeMeses: number[] = (data.mesesPeriodo && data.mesesPeriodo.length > 0)
    ? [...data.mesesPeriodo].sort((a, b) => a - b)
    : Array.from(new Set(itens.map((x) => x.mes_num).filter(Boolean))).sort((a, b) => a - b)

  const mesesList = activeMeses.length > 0 ? activeMeses : [1]

  // Estrutura de agrupamento pivot: (distribuidor, familia)
  interface PivotRow {
    dist_id: number
    dist_cod: string | number
    dist_nome: string
    familia_cod: string
    familia_nome: string
    meses: Record<number, {
      caixas: number
      valor: number
      cobertura: number
    }>
  }

  const pivotMap = new Map<string, PivotRow>()

  itens.forEach((item) => {
    const key = `${item.dist_id}__${item.familia_cod || item.familia_nome}`
    let row = pivotMap.get(key)
    if (!row) {
      row = {
        dist_id: item.dist_id,
        dist_cod: item.cod_dist ?? item.dist_id,
        dist_nome: item.dist_nome || '',
        familia_cod: item.familia_cod || '',
        familia_nome: item.familia_nome || '',
        meses: {},
      }
      pivotMap.set(key, row)
    }
    const current = row.meses[item.mes_num] || { caixas: 0, valor: 0, cobertura: 0 }
    row.meses[item.mes_num] = {
      caixas: current.caixas + (item.total_caixas || 0),
      valor: current.valor + (item.total_valor || 0),
      cobertura: current.cobertura + (item.total_cobertura || 0),
    }
  })

  // Ordenação: primeiro por nome do distribuidor, depois por nome da família
  const sortedRows = Array.from(pivotMap.values()).sort((a, b) => {
    const compDist = a.dist_nome.localeCompare(b.dist_nome, 'pt-BR')
    if (compDist !== 0) return compDist
    return a.familia_nome.localeCompare(b.familia_nome, 'pt-BR')
  })

  // Cabeçalhos base solicitados
  const baseHeaders = [
    { label: 'Distribuidor Cod', align: 'center' as const },
    { label: 'Distribuidor Nome', align: 'left' as const },
    { label: 'Família Cod', align: 'center' as const },
    { label: 'Família Nome', align: 'left' as const },
  ]

  const monthHeaders = mesesList.map((m) => ({
    label: MONTH_NAMES[m - 1] || `Mês ${m}`,
    align: 'right' as const,
  }))

  const fullHeaders = [
    ...baseHeaders,
    ...monthHeaders,
    { label: 'Total', align: 'right' as const },
  ]

  // Agrupamento por Distribuidor e Categoria para as Abas Mensais
  interface DistGroupData {
    dist_id: number
    dist_cod: string | number
    dist_nome: string
    categorias: Record<string, typeof sortedRows>
  }

  const distGroupsMap = new Map<number, DistGroupData>()
  sortedRows.forEach((r) => {
    let dg = distGroupsMap.get(r.dist_id)
    if (!dg) {
      dg = {
        dist_id: r.dist_id,
        dist_cod: r.dist_cod,
        dist_nome: r.dist_nome,
        categorias: {},
      }
      CATEGORIAS_ORDEM.forEach((cat) => { dg!.categorias[cat] = [] })
      distGroupsMap.set(r.dist_id, dg)
    }
    const cat = determinarCategoria(r.familia_nome)
    if (!dg.categorias[cat]) dg.categorias[cat] = []
    dg.categorias[cat].push(r)
  })

  const sortedDistGroups = Array.from(distGroupsMap.values()).sort((a, b) =>
    a.dist_nome.localeCompare(b.dist_nome, 'pt-BR')
  )

  // 1. Tabela 'Sellout CX'
  const rowsCX: any[][] = []
  const colTotalsCX: Record<number, number> = {}
  let grandTotalCX = 0
  mesesList.forEach((m) => { colTotalsCX[m] = 0 })

  let cxRowIdx = 0
  sortedDistGroups.forEach((dg) => {
    CATEGORIAS_ORDEM.forEach((catNome) => {
      const fams = dg.categorias[catNome] || []
      if (fams.length === 0) return

      const catMonthTotals: Record<number, number> = {}
      let catTotal = 0
      mesesList.forEach((m) => { catMonthTotals[m] = 0 })

      fams.forEach((f) => {
        mesesList.forEach((m) => {
          const val = f.meses[m]?.caixas || 0
          catMonthTotals[m] += val
          catTotal += val
          colTotalsCX[m] = (colTotalsCX[m] || 0) + val
        })
      })
      grandTotalCX += catTotal

      // Subtotal da Categoria
      rowsCX.push([
        cellCategoryHeader(String(dg.dist_cod), 'center'),
        cellCategoryHeader(dg.dist_nome, 'left'),
        cellCategoryHeader('—', 'center'),
        cellCategoryHeader(`TOTAL ${catNome.toUpperCase()}`, 'left'),
        ...mesesList.map((m) => cellCategoryNumber(catMonthTotals[m])),
        cellCategoryNumber(catTotal),
      ])

      fams.forEach((r) => {
        const isEven = cxRowIdx % 2 === 1
        cxRowIdx++
        let rowTotal = 0
        const monthCells = mesesList.map((m) => {
          const val = r.meses[m]?.caixas || 0
          rowTotal += val
          return cellNumber(val, isEven)
        })

        rowsCX.push([
          cellText(r.dist_cod, isEven, 'center'),
          cellText(r.dist_nome, isEven, 'left'),
          cellText(r.familia_cod || '—', isEven, 'center'),
          cellText(`  ▸ ${r.familia_nome}`, isEven, 'left'),
          ...monthCells,
          cellNumber(rowTotal, isEven, true),
        ])
      })
    })
  })

  const totalRowCX = [
    cellTotalLabel('TOTAL GERAL', 'center'),
    cellTotalLabel('', 'left'),
    cellTotalLabel('', 'center'),
    cellTotalLabel('', 'left'),
    ...mesesList.map((m) => cellTotalNumber(colTotalsCX[m] || 0)),
    cellTotalNumber(grandTotalCX),
  ]

  const wsSellCX = buildCorporateSheet({
    reportTitle: 'Validação de Sellout — Volume Mensal por Família (CX)',
    periodLabel,
    distributorLabel: distLabel,
    headers: fullHeaders,
    data: rowsCX,
    totalsRow: totalRowCX,
  })
  XLSX.utils.book_append_sheet(wb, wsSellCX, 'Sellout CX')

  // 2. Tabela 'Sellout R$'
  const rowsRS: any[][] = []
  const colTotalsRS: Record<number, number> = {}
  let grandTotalRS = 0
  mesesList.forEach((m) => { colTotalsRS[m] = 0 })

  let rsRowIdx = 0
  sortedDistGroups.forEach((dg) => {
    CATEGORIAS_ORDEM.forEach((catNome) => {
      const fams = dg.categorias[catNome] || []
      if (fams.length === 0) return

      const catMonthTotals: Record<number, number> = {}
      let catTotal = 0
      mesesList.forEach((m) => { catMonthTotals[m] = 0 })

      fams.forEach((f) => {
        mesesList.forEach((m) => {
          const val = f.meses[m]?.valor || 0
          catMonthTotals[m] += val
          catTotal += val
          colTotalsRS[m] = (colTotalsRS[m] || 0) + val
        })
      })
      grandTotalRS += catTotal

      // Subtotal da Categoria
      rowsRS.push([
        cellCategoryHeader(String(dg.dist_cod), 'center'),
        cellCategoryHeader(dg.dist_nome, 'left'),
        cellCategoryHeader('—', 'center'),
        cellCategoryHeader(`TOTAL ${catNome.toUpperCase()}`, 'left'),
        ...mesesList.map((m) => cellCategoryMoney(catMonthTotals[m])),
        cellCategoryMoney(catTotal),
      ])

      fams.forEach((r) => {
        const isEven = rsRowIdx % 2 === 1
        rsRowIdx++
        let rowTotal = 0
        const monthCells = mesesList.map((m) => {
          const val = r.meses[m]?.valor || 0
          rowTotal += val
          return cellMoney(val, isEven)
        })

        rowsRS.push([
          cellText(r.dist_cod, isEven, 'center'),
          cellText(r.dist_nome, isEven, 'left'),
          cellText(r.familia_cod || '—', isEven, 'center'),
          cellText(`  ▸ ${r.familia_nome}`, isEven, 'left'),
          ...monthCells,
          cellMoney(rowTotal, isEven, true),
        ])
      })
    })
  })

  const totalRowRS = [
    cellTotalLabel('TOTAL GERAL', 'center'),
    cellTotalLabel('', 'left'),
    cellTotalLabel('', 'center'),
    cellTotalLabel('', 'left'),
    ...mesesList.map((m) => cellTotalMoney(colTotalsRS[m] || 0)),
    cellTotalMoney(grandTotalRS),
  ]

  const wsSellRS = buildCorporateSheet({
    reportTitle: 'Validação de Sellout — Faturamento Mensal por Família (R$)',
    periodLabel,
    distributorLabel: distLabel,
    headers: fullHeaders,
    data: rowsRS,
    totalsRow: totalRowRS,
  })
  XLSX.utils.book_append_sheet(wb, wsSellRS, 'Sellout R$')

  // 3. Tabela 'Cobertura'
  const rowsCob: any[][] = []
  const colTotalsCob: Record<number, number> = {}
  let grandTotalCob = 0
  mesesList.forEach((m) => { colTotalsCob[m] = 0 })

  let cobMensalRowIdx = 0
  sortedDistGroups.forEach((dg) => {
    CATEGORIAS_ORDEM.forEach((catNome) => {
      const fams = dg.categorias[catNome] || []
      if (fams.length === 0) return

      const catMonthTotals: Record<number, number> = {}
      let catTotal = 0
      mesesList.forEach((m) => { catMonthTotals[m] = 0 })

      fams.forEach((f) => {
        mesesList.forEach((m) => {
          const val = f.meses[m]?.cobertura || 0
          catMonthTotals[m] += val
          catTotal += val
          colTotalsCob[m] = (colTotalsCob[m] || 0) + val
        })
      })
      grandTotalCob += catTotal

      // Subtotal da Categoria
      rowsCob.push([
        cellCategoryHeader(String(dg.dist_cod), 'center'),
        cellCategoryHeader(dg.dist_nome, 'left'),
        cellCategoryHeader('—', 'center'),
        cellCategoryHeader(`TOTAL ${catNome.toUpperCase()}`, 'left'),
        ...mesesList.map((m) => cellCategoryNumber(catMonthTotals[m])),
        cellCategoryNumber(catTotal),
      ])

      fams.forEach((r) => {
        const isEven = cobMensalRowIdx % 2 === 1
        cobMensalRowIdx++
        let rowTotal = 0
        const monthCells = mesesList.map((m) => {
          const val = r.meses[m]?.cobertura || 0
          rowTotal += val
          return cellNumber(val, isEven)
        })

        rowsCob.push([
          cellText(r.dist_cod, isEven, 'center'),
          cellText(r.dist_nome, isEven, 'left'),
          cellText(r.familia_cod || '—', isEven, 'center'),
          cellText(`  ▸ ${r.familia_nome}`, isEven, 'left'),
          ...monthCells,
          cellNumber(rowTotal, isEven, true),
        ])
      })
    })
  })

  const totalRowCob = [
    cellTotalLabel('TOTAL GERAL', 'center'),
    cellTotalLabel('', 'left'),
    cellTotalLabel('', 'center'),
    cellTotalLabel('', 'left'),
    ...mesesList.map((m) => cellTotalNumber(colTotalsCob[m] || 0)),
    cellTotalNumber(grandTotalCob),
  ]

  const wsCobMes = buildCorporateSheet({
    reportTitle: 'Validação de Cobertura — Positivação Mensal de Clientes por Família',
    periodLabel,
    distributorLabel: distLabel,
    headers: fullHeaders,
    data: rowsCob,
    totalsRow: totalRowCob,
  })
  XLSX.utils.book_append_sheet(wb, wsCobMes, 'Cobertura')
}

/**
 * Constrói e anexa a aba 'Dados para Plano Venda Direta' seguindo fielmente a estrutura do modelo
 * com linha amarela de diretrizes, colunas GV/GN/Meta 2027 em branco e desduplicação de habitantes por UF.
 */
export const appendCorporatePlanoVendaDiretaSheet = (
  wb: any,
  data: ValidacaoSelloutResponse,
  periodLabel: string,
  distLabel: string = 'Distribuidores Selecionados'
) => {
  const itens = data.planoVendaDireta || []
  if (itens.length === 0) return

  const sortedItens = [...itens].sort((a, b) => a.dist_nome.localeCompare(b.dist_nome, 'pt-BR'))

  // Total de Habitantes e Potencial somando a cobertura populacional de cada distribuidor no período
  const totalHabitantes = sortedItens.reduce((acc, p) => acc + Number(p.habitantes || 0), 0)
  const totalPotencial = Math.round(totalHabitantes / 1000)

  const sheetData: any[][] = []

  // Linha 0: Título Institucional Superior
  const titleRow: any[] = [cellBannerTitle('Material para Meta de Positivação 2027')]
  for (let c = 1; c < 26; c++) titleRow.push(cellBannerTitle(''))
  sheetData.push(titleRow)

  // Linha 1: Linha de Diretrizes em Amarelo por Categoria
  const yellowRow: any[] = []
  for (let c = 0; c < 8; c++) yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline('Familia: Todos os macarrões (Exceto Dona Raiz)'))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline('Marca Refreskant E Sucos Marca Velly'))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline('Familia: Todos os temperos'))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline('Marca: Sanditos'))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline('Familia: Todas as Batata Palha'))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline('Marca San Chips S/ Palha'))
  yellowRow.push(cellYellowGuideline(''))
  yellowRow.push(cellYellowGuideline(''))
  sheetData.push(yellowRow)

  // Linha 2: Cabeçalho de Agrupamento das Categorias
  const groupRow: any[] = []
  for (let c = 0; c < 8; c++) groupRow.push(cellColHeader('', 'center'))
  groupRow.push(cellBannerSubtitle('Positivação Categorias Mensal'))
  for (let c = 9; c < 26; c++) groupRow.push(cellBannerSubtitle(''))
  sheetData.push(groupRow)

  // Linha 3: Nomes das 6 Categorias (Macarrão, Refresco, Tempero, Salgadinho, Batata Palha, Snacks)
  const catNamesRow: any[] = []
  for (let c = 0; c < 8; c++) catNamesRow.push(cellColHeader('', 'center'))
  const categoriesList = ['Macarrão', 'Refresco', 'Tempero', 'Salgadinho', 'Batata Palha', 'Snacks']
  categoriesList.forEach((catName) => {
    catNamesRow.push(cellColHeader(catName, 'center'))
    catNamesRow.push(cellColHeader('', 'center'))
    catNamesRow.push(cellColHeader('', 'center'))
  })
  sheetData.push(catNamesRow)

  // Linha 4: Cabeçalhos Individuais das Colunas
  const colHeadersRow: any[] = [
    cellColHeader('Distribuidor', 'left'),
    cellColHeader('UF', 'center'),
    cellColHeader('GV', 'center'),
    cellColHeader('GN', 'center'),
    cellColHeader('Habitantes', 'right'),
    cellColHeader('Potencial de Positivação', 'right'),
    cellColHeader('Positivação Total 3 Meses', 'right'),
    cellColHeader('%', 'right'),
  ]
  for (let i = 0; i < 6; i++) {
    colHeadersRow.push(cellColHeader('Total', 'right'))
    colHeadersRow.push(cellColHeader('% 3M', 'right'))
    colHeadersRow.push(cellColHeader('Meta 2027', 'right'))
  }
  sheetData.push(colHeadersRow)

  // Linhas de Dados
  let grandTotalPositivados = 0
  let sumMacarrao = 0
  let sumRefresco = 0
  let sumTempero = 0
  let sumSalgadinho = 0
  let sumBatataPalha = 0
  let sumSnacks = 0

  sortedItens.forEach((p, idx) => {
    const isEven = idx % 2 === 1
    grandTotalPositivados += p.positivacao_total
    sumMacarrao += p.macarrao.total
    sumRefresco += p.refresco.total
    sumTempero += p.tempero.total
    sumSalgadinho += p.salgadinho.total
    sumBatataPalha += p.batata_palha.total
    sumSnacks += p.snacks.total

    const rowCells: any[] = [
      cellText(p.dist_nome, isEven, 'left', true),
      cellText(p.uf || '—', isEven, 'center'),
      cellText('', isEven, 'center'),
      cellText('', isEven, 'center'),
      cellNumber(p.habitantes, isEven),
      cellNumber(p.potencial, isEven),
      cellNumber(p.positivacao_total, isEven),
      cellPercentWithAlert(p.atingimento_pct, isEven),
      // Macarrão
      cellNumber(p.macarrao.total, isEven),
      cellPercentWithAlert(p.macarrao.pct_3m, isEven),
      cellText('', isEven, 'center'),
      // Refresco
      cellNumber(p.refresco.total, isEven),
      cellPercentWithAlert(p.refresco.pct_3m, isEven),
      cellText('', isEven, 'center'),
      // Tempero
      cellNumber(p.tempero.total, isEven),
      cellPercentWithAlert(p.tempero.pct_3m, isEven),
      cellText('', isEven, 'center'),
      // Salgadinho
      cellNumber(p.salgadinho.total, isEven),
      cellPercentWithAlert(p.salgadinho.pct_3m, isEven),
      cellText('', isEven, 'center'),
      // Batata Palha
      cellNumber(p.batata_palha.total, isEven),
      cellPercentWithAlert(p.batata_palha.pct_3m, isEven),
      cellText('', isEven, 'center'),
      // Snacks
      cellNumber(p.snacks.total, isEven),
      cellPercentWithAlert(p.snacks.pct_3m, isEven),
      cellText('', isEven, 'center'),
    ]

    sheetData.push(rowCells)
  })

  // Linha de Total Geral (Rodapé)
  const totalRow: any[] = [
    cellTotalLabel('Total', 'center'),
    cellTotalLabel('', 'center'),
    cellTotalLabel('', 'center'),
    cellTotalLabel('', 'center'),
    cellTotalNumber(totalHabitantes),
    cellTotalNumber(totalPotencial),
    cellTotalNumber(grandTotalPositivados),
    cellTotalPercent(totalPotencial > 0 ? (grandTotalPositivados / totalPotencial) * 100 : 0),
    // Macarrão
    cellTotalNumber(sumMacarrao),
    cellTotalPercent(grandTotalPositivados > 0 ? (sumMacarrao / grandTotalPositivados) * 100 : 0),
    cellTotalLabel('', 'center'),
    // Refresco
    cellTotalNumber(sumRefresco),
    cellTotalPercent(grandTotalPositivados > 0 ? (sumRefresco / grandTotalPositivados) * 100 : 0),
    cellTotalLabel('', 'center'),
    // Tempero
    cellTotalNumber(sumTempero),
    cellTotalPercent(grandTotalPositivados > 0 ? (sumTempero / grandTotalPositivados) * 100 : 0),
    cellTotalLabel('', 'center'),
    // Salgadinho
    cellTotalNumber(sumSalgadinho),
    cellTotalPercent(grandTotalPositivados > 0 ? (sumSalgadinho / grandTotalPositivados) * 100 : 0),
    cellTotalLabel('', 'center'),
    // Batata Palha
    cellTotalNumber(sumBatataPalha),
    cellTotalPercent(grandTotalPositivados > 0 ? (sumBatataPalha / grandTotalPositivados) * 100 : 0),
    cellTotalLabel('', 'center'),
    // Snacks
    cellTotalNumber(sumSnacks),
    cellTotalPercent(grandTotalPositivados > 0 ? (sumSnacks / grandTotalPositivados) * 100 : 0),
    cellTotalLabel('', 'center'),
  ]
  sheetData.push(totalRow)

  const ws = XLSX.utils.aoa_to_sheet(sheetData)

  // Mesclagens de Células
  ws['!merges'] = [
    // Título Superior
    { s: { r: 0, c: 0 }, e: { r: 0, c: 25 } },
    // Banner Amarelo Diretrizes
    { s: { r: 1, c: 0 }, e: { r: 1, c: 7 } },
    { s: { r: 1, c: 8 }, e: { r: 1, c: 10 } },
    { s: { r: 1, c: 11 }, e: { r: 1, c: 13 } },
    { s: { r: 1, c: 14 }, e: { r: 1, c: 16 } },
    { s: { r: 1, c: 17 }, e: { r: 1, c: 19 } },
    { s: { r: 1, c: 20 }, e: { r: 1, c: 22 } },
    { s: { r: 1, c: 23 }, e: { r: 1, c: 25 } },
    // Agrupamento Positivação Categorias Mensal
    { s: { r: 2, c: 8 }, e: { r: 2, c: 25 } },
    // Nomes das Categorias
    { s: { r: 3, c: 8 }, e: { r: 3, c: 10 } },
    { s: { r: 3, c: 11 }, e: { r: 3, c: 13 } },
    { s: { r: 3, c: 14 }, e: { r: 3, c: 16 } },
    { s: { r: 3, c: 17 }, e: { r: 3, c: 19 } },
    { s: { r: 3, c: 20 }, e: { r: 3, c: 22 } },
    { s: { r: 3, c: 23 }, e: { r: 3, c: 25 } },
  ]

  // Largura das Colunas
  ws['!cols'] = [
    { wch: 32 }, // Distribuidor
    { wch: 8 },  // UF
    { wch: 8 },  // GV
    { wch: 8 },  // GN
    { wch: 16 }, // Habitantes
    { wch: 18 }, // Potencial de Positivação
    { wch: 18 }, // Positivação Total 3 Meses
    { wch: 10 }, // %
    // Macarrão
    { wch: 12 }, { wch: 10 }, { wch: 12 },
    // Refresco
    { wch: 12 }, { wch: 10 }, { wch: 12 },
    // Tempero
    { wch: 12 }, { wch: 10 }, { wch: 12 },
    // Salgadinho
    { wch: 12 }, { wch: 10 }, { wch: 12 },
    // Batata Palha
    { wch: 12 }, { wch: 10 }, { wch: 12 },
    // Snacks
    { wch: 12 }, { wch: 10 }, { wch: 12 },
  ]

  // Altura das Linhas
  const dataRowCount = sortedItens.length
  ws['!rows'] = [
    { hpt: 30 }, // Título
    { hpt: 26 }, // Diretrizes Amarelo
    { hpt: 22 }, // Agrupamento Categorias
    { hpt: 22 }, // Categorias
    { hpt: 24 }, // Headers
    ...Array(dataRowCount).fill({ hpt: 20 }),
    { hpt: 24 }, // Total Geral
  ]

  XLSX.utils.book_append_sheet(wb, ws, 'Dados para Plano Venda Direta')
}

/**
 * Anexa a aba 'Cobertura Ano Categorias' contendo a positivação única acumulada de todos os meses selecionados
 * por categoria (incluindo Dona Raiz) e o bloco de resumo financeiro ao final.
 */
export const appendCorporateCoberturaAnoCategoriasSheet = (
  wb: any,
  data: ValidacaoSelloutResponse,
  periodLabel: string,
  distLabel: string = 'Distribuidores Selecionados'
) => {
  const cobData = data.coberturaAnoCategorias
  if (!cobData) return

  const sortedItens = [...(cobData.itens || [])].sort((a, b) => a.dist_nome.localeCompare(b.dist_nome, 'pt-BR'))
  if (sortedItens.length === 0) {
    sortedItens.push({
      dist_id: 0,
      dist_cod: '',
      dist_nome: distLabel,
      positivacao_total: 0,
      faturamento_realizado: 0,
      meta_restante: 0,
      total_financeiro: 0,
      macarrao: { total: 0, pct: 0 },
      refresco: { total: 0, pct: 0 },
      tempero: { total: 0, pct: 0 },
      salgadinho: { total: 0, pct: 0 },
      batata_palha: { total: 0, pct: 0 },
      snacks: { total: 0, pct: 0 },
      dona_raiz: { total: 0, pct: 0 },
      outros: { total: 0, pct: 0, caixas: 0 },
    })
  }
  const totalConsolidado = cobData.totalConsolidado || {
    positivacao_total: 0,
    macarrao: { total: 0, pct: 0 },
    refresco: { total: 0, pct: 0 },
    tempero: { total: 0, pct: 0 },
    salgadinho: { total: 0, pct: 0 },
    batata_palha: { total: 0, pct: 0 },
    snacks: { total: 0, pct: 0 },
    dona_raiz: { total: 0, pct: 0 },
    outros: { total: 0, pct: 0, caixas: 0 },
  }
  const financeiro = cobData.financeiro || {
    fatRealizadoMesesSelecionados: 0,
    metaRestanteMeses: 0,
    total: 0,
  }

  const sheetData: any[][] = []

  // Linha 0: Título Superior
  const titleRow: any[] = [cellBannerTitle('Validação de Cobertura — Positivação por Categoria (Acumulado Ano)')]
  for (let c = 1; c < 18; c++) titleRow.push(cellBannerTitle(''))
  sheetData.push(titleRow)

  // Linha 1: Diretrizes em Amarelo por Categoria
  const yellowRow: any[] = [
    cellYellowGuideline(''),
    cellYellowGuideline(''),
    cellYellowGuideline('Familia: Todos os macarrões (Exceto Dona Raiz)'),
    cellYellowGuideline(''),
    cellYellowGuideline('Marca Refreskant E Sucos Marca Velly'),
    cellYellowGuideline(''),
    cellYellowGuideline('Familia: Todos os temperos'),
    cellYellowGuideline(''),
    cellYellowGuideline('Marca: Sanditos'),
    cellYellowGuideline(''),
    cellYellowGuideline('Familia: Todas as Batata Palha'),
    cellYellowGuideline(''),
    cellYellowGuideline('Marca San Chips S/ Palha'),
    cellYellowGuideline(''),
    cellYellowGuideline('Marca / Familia: Dona Raiz'),
    cellYellowGuideline(''),
    cellYellowGuideline('Demais Categorias / Fora de Escopo'),
    cellYellowGuideline(''),
  ]
  sheetData.push(yellowRow)

  // Linha 2: Cabeçalho de Agrupamento das Categorias
  const groupRow: any[] = [
    cellColHeader('', 'center'),
    cellColHeader('', 'center'),
  ]
  groupRow.push(cellBannerSubtitle('Positivação Categorias — Acumulado do Ano'))
  for (let c = 3; c < 18; c++) groupRow.push(cellBannerSubtitle(''))
  sheetData.push(groupRow)

  // Linha 3: Nomes das 8 Categorias (Macarrão, Refresco, Tempero, Salgadinho, Batata Palha, Snacks, Dona Raiz, Outros)
  const catNamesRow: any[] = [
    cellColHeader('', 'center'),
    cellColHeader('', 'center'),
  ]
  const categoriesList = ['Macarrão', 'Refresco', 'Tempero', 'Salgadinho', 'Batata Palha', 'Snacks', 'Dona Raiz', 'Outros']
  categoriesList.forEach((catName) => {
    catNamesRow.push(cellColHeader(catName, 'center'))
    catNamesRow.push(cellColHeader('', 'center'))
  })
  sheetData.push(catNamesRow)

  // Linha 4: Cabeçalhos Individuais das Colunas
  const colHeadersRow: any[] = [
    cellColHeader('Distribuidor', 'left'),
    cellColHeader('Positivação Total', 'right'),
  ]
  for (let i = 0; i < 8; i++) {
    colHeadersRow.push(cellColHeader('Total', 'right'))
    colHeadersRow.push(cellColHeader('%', 'right'))
  }
  sheetData.push(colHeadersRow)

  // Linhas de Dados (Distribuidor)
  sortedItens.forEach((p, idx) => {
    const isEven = idx % 2 === 1
    const rowCells: any[] = [
      cellText(p.dist_nome, isEven, 'left', true),
      cellNumber(p.positivacao_total, isEven),
      // Macarrão
      cellNumber(p.macarrao.total, isEven),
      cellPercentWithAlert(p.macarrao.pct, isEven),
      // Refresco
      cellNumber(p.refresco.total, isEven),
      cellPercentWithAlert(p.refresco.pct, isEven),
      // Tempero
      cellNumber(p.tempero.total, isEven),
      cellPercentWithAlert(p.tempero.pct, isEven),
      // Salgadinho
      cellNumber(p.salgadinho.total, isEven),
      cellPercentWithAlert(p.salgadinho.pct, isEven),
      // Batata Palha
      cellNumber(p.batata_palha.total, isEven),
      cellPercentWithAlert(p.batata_palha.pct, isEven),
      // Snacks
      cellNumber(p.snacks.total, isEven),
      cellPercentWithAlert(p.snacks.pct, isEven),
      // Dona Raiz
      cellNumber(p.dona_raiz.total, isEven),
      cellPercentWithAlert(p.dona_raiz.pct, isEven),
      // Outros
      cellNumber(p.outros?.caixas ?? p.outros?.total ?? 0, isEven),
      cellPercentWithAlert(p.outros?.pct ?? 0, isEven),
    ]
    sheetData.push(rowCells)
  })

  // Linha de Total Geral Desduplicado (Rodapé)
  const totalRow: any[] = [
    cellTotalLabel('Total', 'center'),
    cellTotalNumber(totalConsolidado.positivacao_total),
    // Macarrão
    cellTotalNumber(totalConsolidado.macarrao.total),
    cellTotalPercent(totalConsolidado.macarrao.pct),
    // Refresco
    cellTotalNumber(totalConsolidado.refresco.total),
    cellTotalPercent(totalConsolidado.refresco.pct),
    // Tempero
    cellTotalNumber(totalConsolidado.tempero.total),
    cellTotalPercent(totalConsolidado.tempero.pct),
    // Salgadinho
    cellTotalNumber(totalConsolidado.salgadinho.total),
    cellTotalPercent(totalConsolidado.salgadinho.pct),
    // Batata Palha
    cellTotalNumber(totalConsolidado.batata_palha.total),
    cellTotalPercent(totalConsolidado.batata_palha.pct),
    // Snacks
    cellTotalNumber(totalConsolidado.snacks.total),
    cellTotalPercent(totalConsolidado.snacks.pct),
    // Dona Raiz
    cellTotalNumber(totalConsolidado.dona_raiz.total),
    cellTotalPercent(totalConsolidado.dona_raiz.pct),
    // Outros
    cellTotalNumber(totalConsolidado.outros?.caixas ?? totalConsolidado.outros?.total ?? 0),
    cellTotalPercent(totalConsolidado.outros?.pct ?? 0),
  ]
  sheetData.push(totalRow)

  // Linha Separadora em Branco (1 linha de espaço conforme solicitado)
  const blankRow: any[] = []
  for (let c = 0; c < 18; c++) blankRow.push(cellText('', false, 'left'))
  sheetData.push(blankRow)

  // Bloco de Resumo Financeiro
  const finHeaderRow: any[] = [
    cellBannerSubtitle('RESUMO FINANCEIRO (R$)'),
    cellBannerSubtitle(''),
  ]
  for (let c = 2; c < 18; c++) finHeaderRow.push(cellText('', false, 'left'))
  sheetData.push(finHeaderRow)

  // 1. Faturamento R$ dos meses selecionados
  const finFatRow: any[] = [
    cellText('Faturamento R$ dos meses selecionados', false, 'left', true),
    cellMoney(financeiro.fatRealizadoMesesSelecionados, false, true),
  ]
  for (let c = 2; c < 18; c++) finFatRow.push(cellText('', false, 'left'))
  sheetData.push(finFatRow)

  // 2. Meta em R$ somada dos meses restantes
  const finMetaRow: any[] = [
    cellText('Meta em R$ somada dos meses restantes', true, 'left', true),
    cellMoney(financeiro.metaRestanteMeses, true, true),
  ]
  for (let c = 2; c < 18; c++) finMetaRow.push(cellText('', true, 'left'))
  sheetData.push(finMetaRow)

  // 3. Total (Realizado + Meta Restante)
  const finTotalRow: any[] = [
    cellTotalLabel('Total', 'left'),
    cellTotalMoney(financeiro.total),
  ]
  for (let c = 2; c < 18; c++) finTotalRow.push(cellTotalLabel('', 'left'))
  sheetData.push(finTotalRow)

  // Linha Separadora em Branco (entre Resumo Total e Resumo por Distribuidor)
  const blankRow2: any[] = []
  for (let c = 0; c < 18; c++) blankRow2.push(cellText('', false, 'left'))
  sheetData.push(blankRow2)

  // Bloco de Resumo Financeiro por Distribuidor
  const finDistHeaderBanner: any[] = [
    cellBannerSubtitle('RESUMO FINANCEIRO POR DISTRIBUIDOR (R$)'),
    cellBannerSubtitle(''),
    cellBannerSubtitle(''),
    cellBannerSubtitle(''),
  ]
  for (let c = 4; c < 18; c++) finDistHeaderBanner.push(cellText('', false, 'left'))
  sheetData.push(finDistHeaderBanner)

  const finDistColsHeader: any[] = [
    cellColHeader('Distribuidor', 'left'),
    cellColHeader('Faturamento R$ Meses Selecionados', 'right'),
    cellColHeader('Meta R$ Meses Restantes', 'right'),
    cellColHeader('Total', 'right'),
  ]
  for (let c = 4; c < 18; c++) finDistColsHeader.push(cellText('', false, 'left'))
  sheetData.push(finDistColsHeader)

  sortedItens.forEach((p, idx) => {
    const isEven = idx % 2 === 1
    const fat = Number(p.faturamento_realizado || 0)
    const meta = Number(p.meta_restante || 0)
    const tot = Number(p.total_financeiro !== undefined ? p.total_financeiro : (fat + meta))
    const distRow: any[] = [
      cellText(p.dist_nome, isEven, 'left', true),
      cellMoney(fat, isEven),
      cellMoney(meta, isEven),
      cellMoney(tot, isEven, true),
    ]
    for (let c = 4; c < 18; c++) distRow.push(cellText('', isEven, 'left'))
    sheetData.push(distRow)
  })

  // Rodapé Total do Resumo Financeiro por Distribuidor
  const finDistTotalRow: any[] = [
    cellTotalLabel('Total', 'left'),
    cellTotalMoney(financeiro.fatRealizadoMesesSelecionados),
    cellTotalMoney(financeiro.metaRestanteMeses),
    cellTotalMoney(financeiro.total),
  ]
  for (let c = 4; c < 18; c++) finDistTotalRow.push(cellTotalLabel('', 'left'))
  sheetData.push(finDistTotalRow)

  const ws = XLSX.utils.aoa_to_sheet(sheetData)

  const finStartRow = 5 + sortedItens.length + 2
  const finDistStartRow = finStartRow + 5

  // Mesclagens de Células
  ws['!merges'] = [
    // Título Superior (Linha 0)
    { s: { r: 0, c: 0 }, e: { r: 0, c: 17 } },
    // Banner Amarelo Diretrizes (Linha 1)
    { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
    { s: { r: 1, c: 2 }, e: { r: 1, c: 3 } },
    { s: { r: 1, c: 4 }, e: { r: 1, c: 5 } },
    { s: { r: 1, c: 6 }, e: { r: 1, c: 7 } },
    { s: { r: 1, c: 8 }, e: { r: 1, c: 9 } },
    { s: { r: 1, c: 10 }, e: { r: 1, c: 11 } },
    { s: { r: 1, c: 12 }, e: { r: 1, c: 13 } },
    { s: { r: 1, c: 14 }, e: { r: 1, c: 15 } },
    { s: { r: 1, c: 16 }, e: { r: 1, c: 17 } },
    // Agrupamento Positivação Categorias Acumulado Ano (Linha 2)
    { s: { r: 2, c: 2 }, e: { r: 2, c: 17 } },
    // Nomes das Categorias (Linha 3)
    { s: { r: 3, c: 2 }, e: { r: 3, c: 3 } },
    { s: { r: 3, c: 4 }, e: { r: 3, c: 5 } },
    { s: { r: 3, c: 6 }, e: { r: 3, c: 7 } },
    { s: { r: 3, c: 8 }, e: { r: 3, c: 9 } },
    { s: { r: 3, c: 10 }, e: { r: 3, c: 11 } },
    { s: { r: 3, c: 12 }, e: { r: 3, c: 13 } },
    { s: { r: 3, c: 14 }, e: { r: 3, c: 15 } },
    { s: { r: 3, c: 16 }, e: { r: 3, c: 17 } },
    // Resumo Financeiro Banner
    { s: { r: finStartRow, c: 0 }, e: { r: finStartRow, c: 1 } },
    // Resumo Financeiro por Distribuidor Banner
    { s: { r: finDistStartRow, c: 0 }, e: { r: finDistStartRow, c: 3 } },
  ]

  // Largura das Colunas
  ws['!cols'] = [
    { wch: 38 }, // Distribuidor
    { wch: 24 }, // Positivação Total / Resumo Financeiro / Faturamento
    // Macarrão / Meta R$ Meses Restantes / Total
    { wch: 16 }, { wch: 14 },
    // Refresco
    { wch: 12 }, { wch: 10 },
    // Tempero
    { wch: 12 }, { wch: 10 },
    // Salgadinho
    { wch: 12 }, { wch: 10 },
    // Batata Palha
    { wch: 12 }, { wch: 10 },
    // Snacks
    { wch: 12 }, { wch: 10 },
    // Dona Raiz
    { wch: 12 }, { wch: 10 },
    // Outros
    { wch: 12 }, { wch: 10 },
  ]

  // Altura das Linhas
  const dataRowCount = sortedItens.length
  ws['!rows'] = [
    { hpt: 30 }, // Título
    { hpt: 26 }, // Diretrizes Amarelo
    { hpt: 22 }, // Agrupamento Categorias
    { hpt: 22 }, // Categorias
    { hpt: 24 }, // Headers
    ...Array(dataRowCount).fill({ hpt: 20 }),
    { hpt: 24 }, // Total Geral
    { hpt: 16 }, // Linha em Branco 1
    { hpt: 24 }, // Header Resumo Financeiro
    { hpt: 22 }, // Faturamento Meses Selecionados
    { hpt: 22 }, // Meta Meses Restantes
    { hpt: 24 }, // Total Geral Financeiro
    { hpt: 16 }, // Linha em Branco 2
    { hpt: 24 }, // Header Resumo Financeiro por Distribuidor Banner
    { hpt: 22 }, // Col Headers Resumo Distribuidor
    ...Array(dataRowCount).fill({ hpt: 20 }),
    { hpt: 24 }, // Total Geral Financeiro por Distribuidor
  ]

  XLSX.utils.book_append_sheet(wb, ws, 'Cobertura Ano Categorias')
}

// =============================================================================
// PROCESSAMENTO EM LOTE COM MODAL DE PROGRESSO E GERAÇÃO DO ZIP
// =============================================================================

export interface DistribuidorItemOption {
  id: string | number
  name: string
  codDist?: string | number
}

export interface ProcessarRevisaoGrupoOptions {
  distribuidores: DistribuidorItemOption[]
  baseParams: {
    ano: number
    mes: number
    mesCompleto: boolean
    semanas: number[]
    cidades: string[]
    tipoPeriodo?: 'mensal' | 'ytd'
    ytdModo?: 'tudo' | 'custom'
    mesesYtd?: number[]
  }
  onProgress: (current: number, total: number, distName: string) => void
  signal?: AbortSignal
}

// MONTH_NAMES declarado no topo do arquivo

export const processarRevisaoEmGrupo = async ({
  distribuidores,
  baseParams,
  onProgress,
  signal,
}: ProcessarRevisaoGrupoOptions): Promise<void> => {
  if (!distribuidores || distribuidores.length === 0) {
    throw new Error('Nenhum distribuidor selecionado para a revisão em grupo.')
  }

  const zip = new JSZip()
  const total = distribuidores.length

  const isYtd = baseParams.tipoPeriodo === 'ytd'
  const shouldGenerateConsolidado = total > 1 || isYtd
  const totalEtapas = total + (shouldGenerateConsolidado ? 1 : 0)

  const mesNome = MONTH_NAMES[baseParams.mes] || `Mes_${baseParams.mes + 1}`
  const periodLabel = isYtd
    ? baseParams.ytdModo === 'tudo'
      ? `YTD: Jan a ${mesNome} de ${baseParams.ano}`
      : `YTD (${baseParams.mesesYtd?.map((m) => MONTH_NAMES[m]?.substring(0, 3)).join(', ') || mesNome}) de ${baseParams.ano}`
    : `${mesNome} de ${baseParams.ano}`
  const semanasLabel = isYtd
    ? 'Acumulado Ano (YTD)'
    : baseParams.mesCompleto
      ? 'Mês Completo'
      : baseParams.semanas && baseParams.semanas.length > 0
        ? `Semanas: S${baseParams.semanas.join(', S')}`
        : 'Período Selecionado'

  // 1. Processa cada distribuidor individualmente
  for (let i = 0; i < total; i++) {
    if (signal?.aborted) {
      throw new Error('Geração cancelada pelo usuário.')
    }

    const dist = distribuidores[i]
    onProgress(i + 1, totalEtapas, dist.name)

    const params: ValidacaoSelloutParams = {
      distribuidorIds: [dist.id],
      ano: baseParams.ano,
      mes: baseParams.mes,
      tipoPeriodo: baseParams.tipoPeriodo,
      ytdModo: baseParams.ytdModo,
      mesesYtd: baseParams.mesesYtd,
      mesCompleto: baseParams.mesCompleto,
      semanas: baseParams.semanas,
      cidades: baseParams.cidades || [],
    }

    try {
      const data = await fetchValidacaoSellout(params)

      // Se for YTD, gera no formato corporativo completo de 4 abas (Resumo, Categorias, Estoque, Cobertura com Potencial/Gap)
      // Se não for YTD, gera no formato padrão de Sellout vs Média 3M
      const excelBuffer = isYtd
        ? buildResumoCorporativoWorkbook(dist.name, periodLabel, data)
        : buildDistribuidorExcelWorkbook(
            dist.name,
            dist.codDist,
            periodLabel,
            semanasLabel,
            data
          )

      const sanitizedDistName = dist.name
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .substring(0, 30)

      const distCode = dist.codDist || dist.id
      const fileName = `Revisao_Sellout_${distCode}_${sanitizedDistName}_${mesNome}_${baseParams.ano}.xlsx`

      zip.file(fileName, excelBuffer)
    } catch (err: any) {
      console.error(`Erro ao processar distribuidor ${dist.name}:`, err)
      // Adiciona um arquivo txt de log informando o erro pontual sem abortar o restante
      zip.file(`ERRO_${dist.name.replace(/[^a-zA-Z0-9]/g, '_')}.txt`, `Falha ao processar dados deste distribuidor: ${err?.message || 'Erro de conexão'}`)
    }
  }

  // 2. Se houver múltiplos distribuidores ou for YTD, gera a planilha consolidada acumulada de todos os distribuidores selecionados juntos
  if (shouldGenerateConsolidado) {
    if (signal?.aborted) {
      throw new Error('Geração cancelada pelo usuário.')
    }

    onProgress(totalEtapas, totalEtapas, 'Consolidado Geral (Todos os Distribuidores Selecionados)')

    try {
      const allDistIds = distribuidores.map((d) => d.id)
      const paramsConsolidado: ValidacaoSelloutParams = {
        distribuidorIds: allDistIds,
        totalDistribuidoresCount: distribuidores.length,
        ano: baseParams.ano,
        mes: baseParams.mes,
        tipoPeriodo: baseParams.tipoPeriodo,
        ytdModo: baseParams.ytdModo,
        mesesYtd: baseParams.mesesYtd,
        mesCompleto: baseParams.mesCompleto,
        semanas: baseParams.semanas,
        cidades: baseParams.cidades || [],
      }

      const dataConsolidado = await fetchValidacaoSellout(paramsConsolidado)

      const excelConsolidadoBuffer = buildResumoCorporativoWorkbook(
        'Consolidado - Todos os Distribuidores Selecionados',
        periodLabel,
        dataConsolidado
      )

      const consolidadoFileName = `00_Resumo_Consolidado_Todos_Distribuidores_${mesNome}_${baseParams.ano}.xlsx`
      zip.file(consolidadoFileName, excelConsolidadoBuffer)
    } catch (err: any) {
      console.error('Erro ao processar consolidado de distribuidores:', err)
      zip.file('ERRO_Consolidado_Geral.txt', `Falha ao processar dados consolidados do grupo: ${err?.message || 'Erro de conexão'}`)
    }
  }

  if (signal?.aborted) {
    throw new Error('Geração cancelada pelo usuário.')
  }

  // Gera o arquivo ZIP final em memória
  const zipBlob = await zip.generateAsync({ type: 'blob' })

  // Dispara o download no navegador
  const zipUrl = URL.createObjectURL(zipBlob)
  const anchor = document.createElement('a')
  anchor.href = zipUrl
  anchor.download = `Revisao_Grupo_Sellout_${mesNome}_${baseParams.ano}.zip`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(zipUrl)
}
